/**
 * Reads currentUser.new_subscriptions. Dismissing
 * (PUT /api/v2/users/me/subscription_tours) clears every pending tour at
 * once — no per-item dismiss — hence the local tracking in
 * markStepSeen/markSeen below.
 *
 * targetSelector/ctaLabel/route are FE-only, keyed by `key`; a key with no
 * entry in subscriptionTourTargets is silently skipped.
 */

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useCurrentUser from './useCurrentUser';
import RouteHelpers from 'helpers/routes';
import I18nHelper from 'helpers/i18n';
import { dismissSubscriptionTours } from 'actions/userMe';
import { useGetClassConnectEnrollments } from 'actions/enrollments';
import { update } from 'store/slices/user';
import { SERVICE_KEYS } from 'constants/services';

const i18n = new I18nHelper('fe.hooks.useSubscriptionTours');

// Builds a service tile's spotlight selector from the same SERVICE_KEYS type
// ServiceTile.tsx and services.ts use, so a renamed/removed service key is a
// compile error here instead of a tour that silently stops finding its
// target.
function serviceTileSelector(serviceKey: SERVICE_KEYS): string {
  return `[data-spotlight-popover="service-tile-${serviceKey}"]`;
}

function sessionStorageKey(userId: number): string {
  return `subscription_tours_handled_${userId}`;
}

function readHandledKeys(userId: number | undefined): string[] {
  if (typeof window === 'undefined' || !userId) {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(sessionStorageKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeHandledKeys(userId: number | undefined, keys: string[]): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      sessionStorageKey(userId),
      JSON.stringify(keys)
    );
  } catch {
    // Storage full or unavailable — worst case this tour reappears next load.
  }
}

function clearHandledKeys(userId: number | undefined): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }
  window.sessionStorage.removeItem(sessionStorageKey(userId));
}

interface NewSubscription {
  key: string;
  title: string;
  description: string;
}

export interface SubscriptionTour extends NewSubscription {
  targetSelector: string;
  ctaLabel: string;
  // Present only when the CTA should navigate somewhere. Absent (e.g.
  // class_connect's "Got it!") means the CTA just acknowledges this step —
  // Dashboard.tsx always shows the button either way.
  route?: string;
}

interface UseSubscriptionTours {
  tours: SubscriptionTour[];
  isLoading: boolean;
  markSeen: () => void;
  markStepSeen: (key: string) => void;
}

function useSubscriptionTours(): UseSubscriptionTours {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  // Re-synced once currentUser.id loads (it's undefined for a render or two
  // after login, so the initial sessionStorage read can miss).
  const [locallyHandledKeys, setLocallyHandledKeys] = useState<string[]>(() =>
    readHandledKeys(currentUser?.id)
  );

  useEffect(() => {
    if (currentUser?.id) {
      setLocallyHandledKeys(readHandledKeys(currentUser.id));
    }
  }, [currentUser?.id]);

  // Mirrors Services.tsx's getServiceURL() lr_coach branching for the same
  // service tile.
  const isLrCoach = currentUser?.roles?.includes('lr_coach') ?? false;

  // Same queryKey Dashboard.tsx already uses — react-query dedupes, no
  // extra network call. Used below to skip class_connect entirely when
  // there's no enrollment for it to spotlight.
  const { data: classConnectEnrollments, isLoading: isClassConnectLoading } =
    useGetClassConnectEnrollments(currentUser?.id);
  const hasClassConnectEnrollment =
    (classConnectEnrollments?.user_sis_enrollments?.length ?? 0) > 0;

  const subscriptionTourTargets: Record<
    string,
    { targetSelector: string; ctaLabel: string; route?: string }
  > = {
    // 'Learning Resources' service tile — the newer product; legacy
    // PDLibrary is a different, unrelated card.
    learning_resources: {
      targetSelector: serviceTileSelector('Learning Resources'),
      ctaLabel: i18n.t(
        'explore_learning_resources',
        'Explore Learning Resources'
      ),
      route: isLrCoach
        ? RouteHelpers.getPath('coach-learning-resources')
        : RouteHelpers.getPath('learning-resources')
    },
    // serviceKey is 'Environmental Scales', not 'data_collection' —
    // different naming scheme.
    data_collection: {
      targetSelector: serviceTileSelector('Environmental Scales'),
      ctaLabel: i18n.t('explore_data_collection', 'Explore Data Collection'),
      route: RouteHelpers.getPath('measure')
    },
    // 'CLASS Calibration 2.0' service tile, not legacy 'Calibration' — a
    // different product.
    calibrations: {
      targetSelector: serviceTileSelector('CLASS Calibration 2.0'),
      ctaLabel: i18n.t('explore_calibrations', 'Explore Calibrations'),
      route: RouteHelpers.getPath('calibrations-view')
    },
    instant_insights: {
      targetSelector: serviceTileSelector('Instant Insights'),
      ctaLabel: i18n.t('explore_instant_insights', 'Explore Instant Insights'),
      route: RouteHelpers.getPath('improve')
    },
    // No single CLASS Connect landing page and no one course to send every
    // enrollee to, so the CTA just acknowledges the step instead of
    // navigating (no route).
    class_connect: {
      targetSelector: '[data-spotlight-popover="class-connect-section"]',
      ctaLabel: i18n.t('got_it', 'Got it!')
    }
  };

  const newSubscriptions: NewSubscription[] =
    currentUser?.new_subscriptions ?? [];

  const pendingSubscriptions = newSubscriptions.filter(subscription => {
    if (!subscriptionTourTargets[subscription.key]) {
      return false;
    }
    // class-connect-section only renders when the user has an enrollment —
    // skip the tour rather than spotlight a target that isn't there.
    if (subscription.key === 'class_connect' && !hasClassConnectEnrollment) {
      return false;
    }
    return true;
  });

  const tours: SubscriptionTour[] = pendingSubscriptions
    .filter(subscription => !locallyHandledKeys.includes(subscription.key))
    .map(subscription => ({
      ...subscription,
      ...subscriptionTourTargets[subscription.key]
    }));

  function dismissEverything() {
    // Guards against a future caller without Dashboard.tsx's guarantee that
    // this only fires once currentUser is loaded.
    if (!currentUser?.id) {
      return;
    }
    // Optimistic, so the popover closes without waiting on a round trip.
    // update() replaces the whole slice (not a merge), hence spreading
    // currentUser back in.
    dispatch(update({ ...currentUser, new_subscriptions: [] }));
    setLocallyHandledKeys([]);
    clearHandledKeys(currentUser?.id);
    dismissSubscriptionTours().catch(() => {
      // No recovery path needed — worst case the tour just reappears next load.
    });
  }

  // X button / "Done" — end the whole tour now, regardless of what's left.
  function markSeen() {
    dismissEverything();
  }

  // A CTA click — remember just this key locally; dismiss for real only if
  // nothing else is left.
  function markStepSeen(key: string) {
    const updatedHandledKeys = Array.from(
      new Set([...locallyHandledKeys, key])
    );
    const stillPending = pendingSubscriptions.filter(
      subscription => !updatedHandledKeys.includes(subscription.key)
    );

    if (stillPending.length === 0) {
      dismissEverything();
      return;
    }

    setLocallyHandledKeys(updatedHandledKeys);
    writeHandledKeys(currentUser?.id, updatedHandledKeys);
  }

  return {
    tours,
    isLoading: !currentUser?.id || isClassConnectLoading,
    markSeen,
    markStepSeen
  };
}

export default useSubscriptionTours;
